import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Banknote, Building2, ChevronDown, CircleDollarSign, CreditCard,
    Landmark, Plus, ReceiptText, Smartphone, Trash2, WalletCards
} from 'lucide-react';
import "../styles/paymentssection.css";
const money = (value) => Number(value || 0).toLocaleString('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
});

const newCashLine = () => ({
    payment_type: 'cash',
    account_id: '',
    amount: '',
    reference_no: ''
});

async function postJson(url, payload) {
    const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify(payload)
    });
    const text = await response.text();
    let data;
    try {
        data = JSON.parse(text);
    } catch {
        throw new Error(`Accounts API returned invalid JSON (HTTP ${response.status})`);
    }
    if (!response.ok || data.status !== 'success') {
        throw new Error(data.message || `Accounts request failed (HTTP ${response.status})`);
    }
    return data;
}

export function useSalePayments({
    apiUrl,
    shopId,
    username,
    customerId,
    saleId = null,
    invoiceTotal = 0,
    onError = null
}) {
    const [accounts, setAccounts] = useState([]);
    const [previousBalance, setPreviousBalance] = useState(0);
    const [creditBalance, setCreditBalance] = useState(0);
    const [transactionBalance, setTransactionBalance] = useState(0);
    const [payments, setPayments] = useState([newCashLine()]);
    const [loading, setLoading] = useState(false);

    const reportError = useCallback((error) => {
        if (onError) onError(error);
        else console.error(error);
    }, [onError]);

    const refreshAccounts = useCallback(async () => {
        if (!apiUrl || !shopId || !username) {
            setAccounts([]);
            return [];
        }
        const result = await postJson(apiUrl, {
            action: 'list_accounts', shop_id: shopId, username, active_only: true
        });
        const activeAccounts = Array.isArray(result.data) ? result.data : [];
        setAccounts(activeAccounts);
        return activeAccounts;
    }, [apiUrl, shopId, username]);

    useEffect(() => {
        if (!apiUrl || !shopId || !username) {
            setAccounts([]);
            return undefined;
        }
        let active = true;
        postJson(apiUrl, {
            action: 'list_accounts', shop_id: shopId, username, active_only: true
        }).then((result) => {
            if (active) setAccounts(Array.isArray(result.data) ? result.data : []);
        }).catch(reportError);
        return () => { active = false; };
    }, [apiUrl, shopId, username, reportError]);

    useEffect(() => {
        if (!apiUrl || !shopId || !username || !customerId) {
            setPreviousBalance(0);
            setCreditBalance(0);
            setTransactionBalance(0);
            return;
        }
        let active = true;
        setLoading(true);
        postJson(apiUrl, {
            action: 'get_customer_balance',
            shop_id: shopId,
            username,
            customer_id: customerId,
            exclude_sale_id: saleId || null
        }).then((result) => {
            if (!active) return;
            setPreviousBalance(Number(result.data?.previous_balance || 0));
            setCreditBalance(Number(result.data?.credit_balance || 0));
            setTransactionBalance(Number(result.data?.transaction_balance || 0));
        }).catch((error) => {
            if (active) {
                setPreviousBalance(0);
                setCreditBalance(0);
                setTransactionBalance(0);
                reportError(error);
            }
        }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [apiUrl, shopId, username, customerId, saleId, reportError]);

    useEffect(() => {
        if (!apiUrl || !shopId || !username || !saleId) return;
        let active = true;
        postJson(apiUrl, {
            action: 'get_sale_payments', shop_id: shopId, username, sale_id: saleId
        }).then((result) => {
            if (!active || !Array.isArray(result.data) || !result.data.length) return;
            setPayments(result.data.map((line) => ({
                payment_type: line.payment_type,
                account_id: line.account_id || '',
                amount: String(line.amount || ''),
                reference_no: line.reference_no || ''
            })));
        }).catch(reportError);
        return () => { active = false; };
    }, [apiUrl, shopId, username, saleId, reportError]);

    const paidTotal = useMemo(() => payments.reduce(
        (sum, line) => sum + Math.max(0, Number(line.amount || 0)), 0
    ), [payments]);

    const accountReceiptUsage = useMemo(() => payments.reduce((usage, line) => {
        if (line.payment_type === 'cash' || !line.account_id) return usage;
        const accountId = String(line.account_id);
        usage[accountId] = (usage[accountId] || 0) + Math.max(0, Number(line.amount || 0));
        return usage;
    }, {}), [payments]);

    const payableTotal = Math.max(0, Number(previousBalance) - Number(creditBalance) + Number(invoiceTotal || 0));
    const balanceAfter = Math.max(0, payableTotal - paidTotal);

    const updatePayment = useCallback((index, patch) => {
        setPayments((current) => current.map((line, i) => i === index ? { ...line, ...patch } : line));
    }, []);

    const changePaymentType = useCallback((index, paymentType) => {
        updatePayment(index, { payment_type: paymentType, account_id: '', reference_no: '' });
    }, [updatePayment]);

    const addPayment = useCallback(() => {
        setPayments((current) => current.length >= 8 ? current : [...current, newCashLine()]);
    }, []);

    const removePayment = useCallback((index) => {
        setPayments((current) => current.length === 1
            ? [newCashLine()]
            : current.filter((_, i) => i !== index));
    }, []);

    const resetPayments = useCallback(() => setPayments([newCashLine()]), []);

    const payRemaining = useCallback((index = 0) => {
        const otherTotal = payments.reduce((sum, line, i) =>
            i === index ? sum : sum + Math.max(0, Number(line.amount || 0)), 0);
        updatePayment(index, { amount: String(Math.max(0, payableTotal - otherTotal).toFixed(2)) });
    }, [payments, payableTotal, updatePayment]);

    const validate = useCallback(() => {
        if (paidTotal > payableTotal + 0.01) {
            return 'Paid amount cannot be greater than the grand total';
        }
        for (let i = 0; i < payments.length; i += 1) {
            const line = payments[i];
            const amount = Number(line.amount || 0);
            if (amount <= 0) continue;
            if (line.payment_type !== 'cash' && !line.account_id) {
                return `Select an account for payment line ${i + 1}`;
            }
            if (line.payment_type !== 'cash' && !accounts.some((account) => (
                Number(account.account_id) === Number(line.account_id) &&
                account.account_type === line.payment_type && Number(account.is_active) === 1
            ))) {
                return `The account on payment line ${i + 1} is unavailable or inactive`;
            }
        }
        return null;
    }, [payments, paidTotal, payableTotal, accounts]);

    const payload = useMemo(() => ({
        previous_balance: Number(previousBalance.toFixed(2)),
        payable_total: Number(payableTotal.toFixed(2)),
        received_amount: Number(paidTotal.toFixed(2)),
        paid_amount: Number(paidTotal.toFixed(2)),
        payments: payments
            .filter((line) => Number(line.amount || 0) > 0)
            .map((line) => ({
                payment_type: line.payment_type,
                account_id: line.payment_type === 'cash' ? null : Number(line.account_id),
                amount: Number(Number(line.amount).toFixed(2)),
                reference_no: String(line.reference_no || '').trim() || null
            }))
    }), [previousBalance, payableTotal, paidTotal, payments]);

    return {
        accounts, previousBalance, creditBalance, transactionBalance, payments,
        paidTotal, payableTotal, balanceAfter, loading, updatePayment,
        changePaymentType, addPayment, removePayment, resetPayments, payRemaining, validate, payload,
        setPayments, accountReceiptUsage, refreshAccounts
    };
}

export function usePurchasePayments({
    apiUrl,
    shopId,
    username,
    supplierId,
    purchaseId = null,
    invoiceTotal = 0,
    onError = null
}) {
    const [accounts, setAccounts] = useState([]);
    const [payments, setPayments] = useState([newCashLine()]);
    const [supplierBalance, setSupplierBalance] = useState({
        signed_balance: 0,
        previous_balance: 0,
        credit_balance: 0
    });
    const [loading, setLoading] = useState(false);

    const reportError = useCallback((error) => {
        if (onError) onError(error);
        else console.error(error);
    }, [onError]);

    const refreshAccounts = useCallback(async () => {
        if (!apiUrl || !shopId || !username) {
            setAccounts([]);
            return [];
        }
        const result = await postJson(apiUrl, {
            action: 'list_accounts', shop_id: shopId, username, active_only: true
        });
        const rows = Array.isArray(result.data) ? result.data : [];
        setAccounts(rows);
        return rows;
    }, [apiUrl, shopId, username]);

    useEffect(() => {
        let active = true;
        refreshAccounts().catch(reportError);
        return () => { active = false; void active; };
    }, [refreshAccounts, reportError]);

    useEffect(() => {
        if (!apiUrl || !shopId || !username || !supplierId) {
            setSupplierBalance({ signed_balance: 0, previous_balance: 0, credit_balance: 0 });
            return undefined;
        }
        let active = true;
        setLoading(true);
        postJson(apiUrl, {
            action: 'get_supplier_balance',
            shop_id: shopId,
            username,
            supplier_id: supplierId,
            exclude_purchase_id: purchaseId || null
        }).then((result) => {
            if (!active) return;
            setSupplierBalance({
                signed_balance: Number(result.data?.signed_balance || 0),
                previous_balance: Number(result.data?.previous_balance || 0),
                credit_balance: Number(result.data?.credit_balance || 0)
            });
        }).catch((error) => {
            if (active) {
                setSupplierBalance({ signed_balance: 0, previous_balance: 0, credit_balance: 0 });
                reportError(error);
            }
        }).finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [apiUrl, shopId, username, supplierId, purchaseId, reportError]);

    const paidTotal = useMemo(() => payments.reduce(
        (sum, line) => sum + Math.max(0, Number(line.amount || 0)), 0
    ), [payments]);
    const signedBalance = Number(supplierBalance.signed_balance || 0);
    const previousBalance = Math.max(0, Number(supplierBalance.previous_balance || 0));
    const creditBalance = Math.max(0, Number(supplierBalance.credit_balance || 0));
    const purchaseTotal = Math.max(0, Number(invoiceTotal || 0));
    const payableTotal = Math.max(0, signedBalance + purchaseTotal);
    const previousPaid = Math.min(previousBalance, paidTotal);
    const creditApplied = Math.min(creditBalance, purchaseTotal);
    const currentInvoicePaid = Math.min(
        purchaseTotal,
        Math.max(0, paidTotal - previousPaid) + creditApplied
    );
    const currentInvoiceBalance = Math.max(0, purchaseTotal - currentInvoicePaid);
    const balanceAfter = signedBalance + purchaseTotal - paidTotal;

    const updatePayment = useCallback((index, patch) => {
        setPayments(current => current.map((line, i) => i === index ? { ...line, ...patch } : line));
    }, []);
    const changePaymentType = useCallback((index, paymentType) => {
        updatePayment(index, { payment_type: paymentType, account_id: '', reference_no: '' });
    }, [updatePayment]);
    const addPayment = useCallback(() => {
        setPayments(current => current.length >= 8 ? current : [...current, newCashLine()]);
    }, []);
    const removePayment = useCallback((index) => {
        setPayments(current => current.length === 1
            ? [newCashLine()]
            : current.filter((_, i) => i !== index));
    }, []);
    const resetPayments = useCallback(() => setPayments([newCashLine()]), []);
    const payRemaining = useCallback((index = 0) => {
        const otherTotal = payments.reduce((sum, line, i) =>
            i === index ? sum : sum + Math.max(0, Number(line.amount || 0)), 0);
        updatePayment(index, { amount: String(Math.max(0, payableTotal - otherTotal).toFixed(2)) });
    }, [payments, payableTotal, updatePayment]);

    const loadSavedPurchase = useCallback((purchase) => {
        let saved = [];
        try {
            saved = Array.isArray(purchase?.payments)
                ? purchase.payments
                : (Array.isArray(purchase?.payment_breakdown)
                    ? purchase.payment_breakdown
                    : JSON.parse(purchase?.payment_breakdown || '[]'));
        } catch {
            saved = [];
        }
        const fallbackPaid = Number(purchase?.paid_now ?? purchase?.received_amount ?? 0);
        if (!saved.length && fallbackPaid > 0) {
            saved = [{
                payment_type: purchase?.payment_method === 'bank_transfer'
                    ? 'bank_account'
                    : (purchase?.payment_method === 'mobile_payment' ? 'digital_wallet' : 'cash'),
                account_id: '', amount: fallbackPaid, reference_no: ''
            }];
        }
        setPayments(saved.length ? saved.map(line => ({
            payment_type: line.payment_type || 'cash',
            account_id: line.account_id || '',
            amount: String(line.amount ?? ''),
            reference_no: line.reference_no || ''
        })) : [newCashLine()]);
    }, []);

    const validate = useCallback(() => {
        if (paidTotal > payableTotal + 0.01) {
            return `Payment exceeds total payable. Available payable amount is Rs. ${payableTotal.toFixed(2)}`;
        }
        const usage = {};
        for (let i = 0; i < payments.length; i += 1) {
            const line = payments[i];
            const amount = Number(line.amount || 0);
            if (amount < 0) return `Payment line ${i + 1} cannot be negative`;
            if (amount <= 0) continue;
            if (line.payment_type !== 'cash' && !line.account_id) {
                return `Select an account for payment line ${i + 1}`;
            }
            if (line.payment_type !== 'cash') {
                const account = accounts.find(item => (
                    Number(item.account_id) === Number(line.account_id) &&
                    item.account_type === line.payment_type && Number(item.is_active) === 1
                ));
                if (!account) return `The account on payment line ${i + 1} is unavailable or inactive`;
                const key = String(account.account_id);
                usage[key] = (usage[key] || 0) + amount;
                const available = Math.max(0, Number(account.current_balance || 0));
                if (usage[key] > available + 0.005) {
                    return `Payment exceeds ${account.provider_name || account.account_type} — ${account.account_title || 'account'}. Available: Rs. ${available.toFixed(2)}; requested: Rs. ${usage[key].toFixed(2)}`;
                }
            }
        }
        return null;
    }, [paidTotal, payableTotal, payments, accounts]);

    const payload = useMemo(() => ({
        paid: Number(currentInvoicePaid.toFixed(2)),
        paid_now: Number(paidTotal.toFixed(2)),
        received_amount: Number(paidTotal.toFixed(2)),
        previous_balance: Number(previousBalance.toFixed(2)),
        payable_total: Number(payableTotal.toFixed(2)),
        payments: payments.filter(line => Number(line.amount || 0) > 0).map(line => ({
            payment_type: line.payment_type,
            account_id: line.payment_type === 'cash' ? null : Number(line.account_id),
            amount: Number(Number(line.amount).toFixed(2)),
            reference_no: String(line.reference_no || '').trim() || null
        }))
    }), [currentInvoicePaid, paidTotal, previousBalance, payableTotal, payments]);

    return {
        accounts, payments, supplierBalance, loading, paidTotal, previousBalance,
        creditBalance, payableTotal, currentInvoicePaid, currentInvoiceBalance,
        balanceAfter, updatePayment, changePaymentType, addPayment, removePayment,
        resetPayments, payRemaining, setPayments, validate, payload, refreshAccounts,
        loadSavedPurchase
    };
}

export function PaymentSection({
    payment,
    subtotal = 0,
    discount = 0,
    onDiscountChange = null,
    disabled = false,
    currency = 'Rs.',
    translations = {},
    direction = 'in',
    title = null,
    helpText = null,
    previousBalance = null,
    creditBalance = null,
    payableTotal = null,
    paidTotal = null,
    balanceAfter = null,
    showDiscount = true,
    showBalance = true,
    showStatus = true,
    referenceEnabled = false,
    summaryRows = [],
    error = '',
    statusText = null,
    statusTone = null
}) {
    const accounts = Array.isArray(payment?.accounts) ? payment.accounts : [];
    const payments = Array.isArray(payment?.payments) ? payment.payments : [];
    const isSelectableAccount = (account) => account.is_active === undefined || Number(account.is_active) === 1;
    const wallets = accounts.filter((a) => a.account_type === 'digital_wallet' && isSelectableAccount(a));
    const banks = accounts.filter((a) => a.account_type === 'bank_account' && isSelectableAccount(a));
    const isOutgoing = direction === 'out';
    const shownPreviousBalance = previousBalance ?? payment.previousBalance ?? 0;
    const shownCreditBalance = creditBalance ?? payment.creditBalance ?? 0;
    const shownPayableTotal = payableTotal ?? payment.payableTotal ?? 0;
    const shownPaidTotal = paidTotal ?? payment.paidTotal ?? 0;
    const shownBalanceAfter = balanceAfter ?? payment.balanceAfter ?? 0;
    const accountUsage = payments.reduce((usage, line) => {
        if (line.payment_type === 'cash' || !line.account_id) return usage;
        const accountId = String(line.account_id);
        usage[accountId] = (usage[accountId] || 0) + Math.max(0, Number(line.amount || 0));
        return usage;
    }, {});

    const paymentTypeMeta = {
        cash: { label: 'Cash', icon: Banknote },
        digital_wallet: { label: 'Digital Wallet', icon: Smartphone },
        bank_account: { label: 'Bank Account', icon: Landmark }
    };

    return (
        <section className={`ozpay-card ${referenceEnabled ? 'ozpay-purchase-card' : ''} ${error ? 'has-payment-error' : ''}`} aria-label="Financial summary and payments">
            <header className="ozpay-card-header">
                <div className="ozpay-title-group">
                    <span className="ozpay-title-icon" aria-hidden="true">
                        <WalletCards size={19} strokeWidth={2} />
                    </span>
                    <div>
                        <h3>{title || translations.paymentDetails || (isOutgoing ? 'Supplier Payment Details' : 'Payment Details')}</h3>
                        <p>{helpText || translations.paymentHelp || (isOutgoing
                            ? 'Pay from one account or split the amount across multiple methods.'
                            : 'Receive with one method or split the payment.')}</p>
                    </div>
                </div>
                <span className="ozpay-secure-badge">
                    <CreditCard size={14} />
                    {payments.length > 1 ? `${payments.length} payment methods` : 'Single payment'}
                </span>
            </header>

            <div className="ozpay-grid">
                <div className="ozpay-left">
                    <div className="ozpay-heading">
                        <div>
                            <span className="ozpay-eyebrow">{translations.paymentMethod || 'Payment Method'}</span>
                            <small>{isOutgoing ? 'Available account balance is checked before saving' : 'Up to 8 payment lines'}</small>
                        </div>
                        <button
                            className="ozpay-add"
                            type="button"
                            onClick={payment.addPayment}
                            disabled={disabled || payments.length >= 8}
                        >
                            <Plus size={15} strokeWidth={2.4} />
                            <span>Split payment</span>
                        </button>
                    </div>

                    {payments.map((line, index) => {
                        const accountOptions = line.payment_type === 'digital_wallet' ? wallets : banks;
                        const typeMeta = paymentTypeMeta[line.payment_type] || paymentTypeMeta.cash;
                        const PaymentTypeIcon = typeMeta.icon;
                        const selectedAccount = accounts.find((account) => (
                            Number(account.account_id) === Number(line.account_id)
                        ));
                        const currentAccountBalance = Number(selectedAccount?.current_balance || 0);
                        const lineAccountUsage = line.account_id
                            ? Number(accountUsage[String(line.account_id)] || 0)
                            : 0;
                        const projectedAccountBalance = currentAccountBalance + (isOutgoing ? -lineAccountUsage : lineAccountUsage);
                        const accountExceeded = Boolean(isOutgoing && selectedAccount && projectedAccountBalance < -0.005);

                        return (
                            <div className={`ozpay-line ${referenceEnabled ? 'ozpay-purchase-line' : ''} ${accountExceeded ? 'has-account-error' : ''}`} key={index}>
                                <div className="ozpay-line-index" title={`Payment line ${index + 1}`}>
                                    {index + 1}
                                </div>

                                <div className="ozpay-field ozpay-type-field">
                                    <label htmlFor={`ozpay-type-${index}`}>Method</label>
                                    <div className="ozpay-control ozpay-control-with-icon">
                                        <PaymentTypeIcon size={16} aria-hidden="true" />
                                        <select
                                            id={`ozpay-type-${index}`}
                                            value={line.payment_type}
                                            onChange={(e) => payment.changePaymentType(index, e.target.value)}
                                            disabled={disabled}
                                            aria-label={`Payment type ${index + 1}`}
                                        >
                                            <option value="cash">Cash</option>
                                            <option value="digital_wallet">Digital Wallet</option>
                                            <option value="bank_account">Bank Account</option>
                                        </select>
                                        <ChevronDown className="ozpay-chevron" size={14} aria-hidden="true" />
                                    </div>
                                </div>

                                <div className="ozpay-field ozpay-account-field">
                                    <label htmlFor={`ozpay-account-${index}`}>{isOutgoing ? 'Pay from' : 'Receive into'}</label>
                                    {line.payment_type !== 'cash' ? (
                                        <div className="ozpay-account-wrap">
                                            <div className="ozpay-control ozpay-control-with-icon">
                                                <Building2 size={16} aria-hidden="true" />
                                                <select
                                                    id={`ozpay-account-${index}`}
                                                    value={line.account_id}
                                                    onChange={(e) => payment.updatePayment(index, { account_id: e.target.value })}
                                                    disabled={disabled}
                                                    required={Number(line.amount || 0) > 0}
                                                    aria-label={`Account ${index + 1}`}
                                                >
                                                    <option value="">Select active account</option>
                                                    {accountOptions.map((account) => (
                                                        <option key={account.account_id} value={account.account_id}>
                                                            {account.provider_name} — {account.account_title} ({account.account_number})
                                                        </option>
                                                    ))}
                                                </select>
                                                <ChevronDown className="ozpay-chevron" size={14} aria-hidden="true" />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="ozpay-cash-destination" id={`ozpay-account-${index}`}>
                                            <Banknote size={16} />
                                            <span>Cash drawer</span>
                                        </div>
                                    )}
                                </div>

                                <div className="ozpay-field ozpay-amount-field">
                                    <label htmlFor={`ozpay-amount-${index}`}>Amount</label>
                                    <div className="ozpay-amount-wrap">
                                        <span className="ozpay-currency">{currency}</span>
                                        <input
                                            id={`ozpay-amount-${index}`}
                                            type="number"
                                            min="0"
                                            max={payment.maximumReceivable ?? shownPayableTotal}
                                            step="0.01"
                                            value={line.amount}
                                            onChange={(e) => payment.updatePayment(index, { amount: e.target.value })}
                                            placeholder="0"
                                            disabled={disabled}
                                            data-no-scan="true"
                                            aria-label={`Amount ${index + 1}`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => payment.payRemaining(index)}
                                            disabled={disabled}
                                            title="Put the remaining amount here"
                                        >
                                            Remaining
                                        </button>
                                    </div>
                                </div>

                                {referenceEnabled && (
                                    <div className="ozpay-field ozpay-reference-field">
                                        <label htmlFor={`ozpay-reference-${index}`}>Reference</label>
                                        <div className="ozpay-control ozpay-control-with-icon">
                                            <ReceiptText size={16} aria-hidden="true" />
                                            <input
                                                id={`ozpay-reference-${index}`}
                                                type="text"
                                                value={line.reference_no || ''}
                                                onChange={(e) => payment.updatePayment(index, { reference_no: e.target.value })}
                                                placeholder="Optional reference"
                                                maxLength={100}
                                                disabled={disabled}
                                                data-no-scan="true"
                                            />
                                        </div>
                                    </div>
                                )}

                                <button
                                    className="ozpay-remove"
                                    type="button"
                                    onClick={() => payment.removePayment(index)}
                                    disabled={disabled}
                                    aria-label={`Remove payment ${index + 1}`}
                                    title="Remove payment line"
                                >
                                    <Trash2 size={16} />
                                </button>

                                {selectedAccount && (
                                    <div className={`ozpay-account-balance ${accountExceeded ? 'is-overdrawn' : ''}`}>
                                        <span>{isOutgoing ? 'Available' : 'Current'} <strong>{currency} {money(currentAccountBalance)}</strong></span>
                                        <span className="ozpay-balance-arrow">{isOutgoing ? '−' : '+'}</span>
                                        <span>{isOutgoing ? 'This payment' : 'Receiving'} <strong>{currency} {money(lineAccountUsage)}</strong></span>
                                        <span className="ozpay-balance-arrow">→</span>
                                        <span className="ozpay-after">After <strong>{currency} {money(projectedAccountBalance)}</strong></span>
                                        {accountExceeded && (
                                            <span className="ozpay-account-warning">
                                                Exceeds available balance by {currency} {money(Math.abs(projectedAccountBalance))}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {error && (
                        <div className="ozpay-inline-error" role="alert">
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="ozpay-right">
                    <div className="ozpay-summary-title">
                        <span className="ozpay-summary-icon"><ReceiptText size={17} /></span>
                        <div>
                            <strong>{translations.financialSummary || 'Financial Summary'}</strong>
                            <small>{isOutgoing ? 'Supplier payable calculation' : 'Live sale calculation'}</small>
                        </div>
                    </div>

                    <div className="ozpay-summary-row ozpay-previous">
                        <span>{translations.previousBalance || (isOutgoing ? 'Previous Supplier Balance' : 'Previous Balance')}:</span>
                        <strong>{payment.loading ? '…' : `${currency} ${money(shownPreviousBalance)}`}</strong>
                    </div>

                    {shownCreditBalance > 0 && (
                        <div className="ozpay-credit">
                            <CircleDollarSign size={15} />
                            {isOutgoing ? 'Supplier advance/credit' : 'Available customer credit'}: {currency} {money(shownCreditBalance)}
                        </div>
                    )}

                    <div className="ozpay-summary-row">
                        <span>{translations.subtotal || (isOutgoing ? 'Current Purchase Total' : 'Subtotal')}:</span>
                        <strong>{currency} {money(subtotal)}</strong>
                    </div>

                    {showDiscount && (
                        <div className="ozpay-summary-row">
                            <span>{translations.discount || 'Discount'}:</span>
                            <input
                                id="ozpay-discount"
                                name="discount"
                                type="number"
                                min="0"
                                step="0.01"
                                value={discount}
                                onChange={onDiscountChange}
                                disabled={disabled}
                                data-no-scan="true"
                                aria-label={translations.discount || 'Discount'}
                            />
                        </div>
                    )}

                    <div className="ozpay-summary-row ozpay-grand">
                        <span>{translations.grandTotal || (isOutgoing ? 'Total Payable' : 'Grand Total')}:</span>
                        <strong>{currency} {money(shownPayableTotal)}</strong>
                    </div>

                    <div className="ozpay-summary-row">
                        <span>{translations.paidAmount || 'Paid Amount'}:</span>
                        <strong>{currency} {money(shownPaidTotal)}</strong>
                    </div>

                    {summaryRows.map((row, index) => (
                        <div className={`ozpay-summary-row ${row.className || 'ozpay-summary-subrow'}`} key={`${row.label}-${index}`}>
                            <span>{row.label}:</span>
                            <strong>{currency} {money(row.value)}</strong>
                        </div>
                    ))}

                    {showBalance && <div className="ozpay-summary-row ozpay-balance">
                        <span>{translations.balance || 'Balance'}:</span>
                        <strong>{currency} {money(shownBalanceAfter)}</strong>
                    </div>}

                    {showStatus && <div className={`ozpay-payment-state ${(statusTone || (shownBalanceAfter <= 0.005 ? 'settled' : 'due')) === 'settled' ? 'is-settled' : 'is-due'}`}>
                        <span className="ozpay-state-dot" />
                        {statusText || (shownBalanceAfter <= 0.005 ? 'Payment settled' : 'Outstanding balance remains')}
                    </div>}
                </div>
            </div>
        </section>
    );
}

export const SalePaymentSection = PaymentSection;
export default PaymentSection;

// Isolated edit controller: opening a sale loads its own saved tender once.
// It never lets a late payment fetch overwrite the user's current edits.
export function useSaleEditPayments({ apiUrl, shopId, username, sale, invoiceTotal = 0 }) {
    const [accounts, setAccounts] = useState([]);
    const [payments, setPayments] = useState([newCashLine()]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadedSale, setLoadedSale] = useState(null);
    const context = sale?.edit_payment_context;
    useEffect(() => {
        let active = true;
        setLoadedSale(null);
        setError('');
        setPayments([newCashLine()]);
        if (!sale) { setLoading(false); return () => { active = false; }; }
        if (!context || !Array.isArray(sale.edit_payments)) {
            setError('Upload the updated sale PHP and shared helper, then reopen this sale.');
            return () => { active = false; };
        }
        setPayments(sale.edit_payments.length ? sale.edit_payments.map(line => ({
            payment_type: line.payment_type, account_id: line.account_id || '',
            amount: String(line.amount ?? ''), reference_no: line.reference_no || ''
        })) : [newCashLine()]);
        setLoading(true);
        postJson(apiUrl, { action: 'list_accounts', shop_id: shopId, username, active_only: true })
            .then(result => {
                if (!active) return;
                // Account preview starts after removing this sale's old receipt.
                const originalByAccount = (sale.edit_posted_payments || []).reduce((map, line) => {
                    if (line.account_id) map[String(line.account_id)] = (map[String(line.account_id)] || 0) + Number(line.amount || 0);
                    return map;
                }, {});
                setAccounts((Array.isArray(result.data) ? result.data : []).map(account => ({
                    ...account, is_active: account.is_active ?? 1,
                    current_balance: Number(account.current_balance || 0) - (originalByAccount[String(account.account_id)] || 0)
                })));
                setLoadedSale(sale);
            }).catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [apiUrl, shopId, username, sale, context]); // sale carries the original server snapshot.
    const cents = value => Math.round(Number(value || 0) * 100) / 100;
    const signedBefore = Number(context?.balance_before || 0);
    const floor = Number(context?.previous_payment_floor || 0);
    const previousBalance = Math.max(floor, signedBefore, 0);
    const creditBalance = Math.max(0, -signedBefore);
    const paidTotal = cents(payments.reduce((sum, line) => sum + cents(line.amount), 0));
    const payableTotal = cents(Math.max(0, signedBefore + Number(invoiceTotal || 0)));
    const maximumReceivable = cents(previousBalance + Number(invoiceTotal || 0));
    const updatePayment = (index, patch) => setPayments(lines => lines.map((line, i) => i === index ? { ...line, ...patch } : line));
    const validate = () => {
        if (error) return error;
        if (loading || loadedSale !== sale) return 'Wait for the saved payment accounts to load.';
        if (payments.length > 8) return 'Use at most eight payment lines.';
        if (floor > 0.005 && paidTotal < floor - 0.005) return `This older sale already paid Rs. ${money(floor)} against previous dues without saving the invoice allocation details. That portion must remain; its payment method can still be changed.`;
        if (paidTotal > maximumReceivable + 0.005) return 'Payment exceeds the invoice and previous dues.';
        for (let i = 0; i < payments.length; i++) {
            const line = payments[i]; const amount = Number(line.amount || 0);
            if (!Number.isFinite(amount) || amount < 0) return `Enter a valid amount on payment line ${i + 1}.`;
            if (!['cash', 'bank_account', 'digital_wallet'].includes(line.payment_type)) return 'Choose a valid payment method.';
            if (amount > 0 && line.payment_type !== 'cash' && !accounts.some(a => String(a.account_id) === String(line.account_id) && a.account_type === line.payment_type && Number(a.is_active) === 1)) return `Select an active matching account on payment line ${i + 1}.`;
            if (String(line.reference_no || '').length > 100) return 'Payment references must be 100 characters or fewer.';
        }
        return null;
    };
    return {
        accounts, payments, paidTotal, previousBalance, creditBalance, payableTotal,
        maximumReceivable, balanceAfter: cents(signedBefore + Number(invoiceTotal || 0) - paidTotal),
        loading: Boolean(sale) && (loading || (!error && loadedSale !== sale)), error,
        updatePayment,
        changePaymentType: (index, payment_type) => updatePayment(index, { payment_type, account_id: '', reference_no: '' }),
        addPayment: () => setPayments(lines => lines.length < 8 ? [...lines, newCashLine()] : lines),
        removePayment: index => setPayments(lines => lines.length === 1 ? [newCashLine()] : lines.filter((_,i) => i !== index)),
        payRemaining: (index = 0) => updatePayment(index, { amount: String(Math.max(0, payableTotal - payments.reduce((sum,line,i) => sum + (i === index ? 0 : cents(line.amount)), 0)).toFixed(2)) }),
        validate,
        payload: {
            received_amount: paidTotal,
            edit_balance_before: signedBefore,
            payments: payments.filter(line => Number(line.amount) > 0).map(line => ({
                payment_type: line.payment_type, account_id: line.payment_type === 'cash' ? null : Number(line.account_id),
                amount: cents(line.amount), reference_no: String(line.reference_no || '').trim() || null
            }))
        }
    };
}
